import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

import { customerService } from '../../../services/api';
import { parseApiFormErrors } from '../../../services/apiErrors';
import BoundedPageLayout from '../../../components/dashboard/BoundedPageLayout';
import PageHeader from '../../../components/dashboard/PageHeader';
import LoadingState from '../../../components/dashboard/LoadingState';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';

const FORM_FIELDS = ['name', 'trigger_phrase'];

// Must match ErrorMessages in the backend's LeadOriginSerializer
// (conversations/api/serializers/lead_origin.py) — swapped for translated
// text instead of showing the backend's raw English string.
const TRIGGER_PHRASE_EMPTY_API_MESSAGE = 'Trigger phrase cannot be empty.';
const TRIGGER_PHRASE_CONFLICT_API_MESSAGE = 'Trigger phrase conflicts with an existing lead origin.';

export default function LeadOriginForm() {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const leadOriginQuery = useQuery({
    queryKey: ['leadOrigin', id],
    queryFn: () => customerService.getLeadOrigin(id).then((res) => res.data),
    enabled: isEditMode,
  });

  const [name, setName] = useState('');
  const [triggerPhrase, setTriggerPhrase] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [touched, setTouched] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');

  useEffect(() => {
    if (isEditMode && leadOriginQuery.data) {
      setName(leadOriginQuery.data.name);
      setTriggerPhrase(leadOriginQuery.data.trigger_phrase ?? '');
    }
  }, [isEditMode, leadOriginQuery.data]);

  const nameTrimmed = name.trim();
  const triggerPhraseTrimmed = triggerPhrase.trim();
  const isValid = nameTrimmed.length > 0 && triggerPhraseTrimmed.length > 0;

  const resetFormFields = () => {
    setName('');
    setTriggerPhrase('');
    setTouched(false);
  };

  const mutation = useMutation({
    mutationFn: () => {
      const payload = { name: nameTrimmed, trigger_phrase: triggerPhraseTrimmed };
      return isEditMode
        ? customerService.updateLeadOrigin(id, payload)
        : customerService.createLeadOrigin(payload);
    },
    // `intent` is whichever submit button was clicked — 'another' only
    // exists in create mode (see the extra button in CardActions).
    onSuccess: (response, intent) => {
      queryClient.invalidateQueries({ queryKey: ['leadOrigins'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['leadOrigin', id] });
        navigate(`/lead-origins/${response.data.uuid}`);
        return;
      }
      if (intent === 'another') {
        resetFormFields();
        setFlashMessage(t('leadOrigins.saveSuccessCreate'));
        return;
      }
      navigate('/lead-origins', { state: { flashMessage: t('leadOrigins.saveSuccessCreate') } });
    },
    onError: (error) => {
      const { fieldErrors: apiFieldErrors, formError: apiFormError } = parseApiFormErrors(error, FORM_FIELDS);
      const translated = { ...apiFieldErrors };
      if (translated.trigger_phrase === TRIGGER_PHRASE_EMPTY_API_MESSAGE) {
        translated.trigger_phrase = t('leadOrigins.validation.triggerPhraseRequired');
      } else if (translated.trigger_phrase === TRIGGER_PHRASE_CONFLICT_API_MESSAGE) {
        translated.trigger_phrase = t('leadOrigins.validation.triggerPhraseDuplicate');
      }
      setFieldErrors(translated);
      setFormError(apiFormError || (Object.keys(translated).length > 0 ? '' : t('leadOrigins.saveError')));
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(true);
    setFieldErrors({});
    setFormError('');
    if (!isValid) return;
    const intent = event.nativeEvent.submitter?.value || 'list';
    mutation.mutate(intent);
  };

  const header = (
    <PageHeader
      title={isEditMode ? t('leadOrigins.editTitle') : t('leadOrigins.createTitle')}
      breadcrumbs={[
        { label: t('leadOrigins.breadcrumb.dashboard') },
        { label: t('nav.groups.settings') },
        { label: t('leadOrigins.breadcrumb.list'), to: '/lead-origins' },
        { label: isEditMode ? t('leadOrigins.breadcrumb.edit') : t('leadOrigins.breadcrumb.new') },
      ]}
    />
  );

  const isLoading = isEditMode && leadOriginQuery.isLoading;

  if (isLoading) {
    return (
      <BoundedPageLayout header={header}>
        <LoadingState message={t('leadOrigins.states.loading')} />
      </BoundedPageLayout>
    );
  }

  return (
    <BoundedPageLayout header={header}>
      <Stack component="form" onSubmit={handleSubmit} spacing={3} sx={{ width: '100%' }}>
        <Card>
          <CardHeader
            title={t('leadOrigins.sections.identification')}
            subheader={t('leadOrigins.sections.identificationHint')}
            titleTypographyProps={{ variant: 'h6' }}
          />
          <Divider />
          <CardContent>
            <Stack spacing={2.5}>
              <TextField
                label={t('leadOrigins.fields.name')}
                helperText={
                  touched && !nameTrimmed
                    ? t('leadOrigins.validation.nameRequired')
                    : fieldErrors.name || t('leadOrigins.fields.nameHelper')
                }
                error={Boolean((touched && !nameTrimmed) || fieldErrors.name)}
                value={name}
                onChange={(event) => {
                  setName(event.target.value);
                  setFieldErrors((prev) => ({ ...prev, name: undefined }));
                }}
                fullWidth
              />
              <TextField
                label={t('leadOrigins.fields.triggerPhrase')}
                helperText={
                  touched && !triggerPhraseTrimmed
                    ? t('leadOrigins.validation.triggerPhraseRequired')
                    : fieldErrors.trigger_phrase || t('leadOrigins.fields.triggerPhraseHelper')
                }
                error={Boolean((touched && !triggerPhraseTrimmed) || fieldErrors.trigger_phrase)}
                value={triggerPhrase}
                onChange={(event) => {
                  setTriggerPhrase(event.target.value);
                  setFieldErrors((prev) => ({ ...prev, trigger_phrase: undefined }));
                }}
                fullWidth
                multiline
                minRows={2}
              />
              {formError && <Alert severity="error">{formError}</Alert>}
            </Stack>
          </CardContent>
          <Divider />
          <CardActions sx={{ px: 3, py: 2, gap: 1, justifyContent: 'flex-end' }}>
            {/* Back to wherever the user came from (list or the lead
                origin's own detail page), not always the list — matches
                what "Cancel" implies: undo getting here. */}
            <Button type="button" color="inherit" onClick={() => navigate(-1)}>
              {t('leadOrigins.actions.cancel')}
            </Button>
            {!isEditMode && (
              <Button
                type="submit"
                name="intent"
                value="another"
                variant="outlined"
                disabled={!isValid || mutation.isPending}
              >
                {t('leadOrigins.actions.saveAndAddAnother')}
              </Button>
            )}
            <Button
              type="submit"
              name="intent"
              value="list"
              variant="contained"
              disabled={!isValid || mutation.isPending}
            >
              {isEditMode ? t('leadOrigins.actions.saveEdit') : t('leadOrigins.actions.saveCreate')}
            </Button>
          </CardActions>
        </Card>
      </Stack>

      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </BoundedPageLayout>
  );
}
